import { ClipboardCheck, Copy, Download, Eye, Inbox, Layers3, Pencil, Plus, Printer, RefreshCw, Save, Search, Trash2, UserRound } from 'lucide-react';
import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiClient } from '../../lib/apiClient';
import {
  DEFAULT_QUESTIONNAIRE_TEMPLATE,
  QUESTIONNAIRE_CONDITION_OPERATORS,
  QUESTIONNAIRE_FIELD_TYPES,
  QUESTIONNAIRE_TEMPLATE_PRESETS,
  cloneQuestionnaireTemplateDefinition,
  createQuestionnaireField,
  createQuestionnairePage,
  getQuestionnairePages,
  getQuestionnaireQuestions,
  isQuestionVisible,
  isQuestionnairePageVisible,
  type QuestionnaireField,
  type QuestionnaireFieldValidation,
  type QuestionnairePage,
  type QuestionnaireShowWhen,
  type QuestionnaireTemplateDefinition,
} from './shared';

const inputClass = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-400';

type QuestionnaireSummary = {
  clinicLegalName?: string;
  preferredSubdomain?: string;
  timezone?: string;
  country?: string;
  billingContactName?: string;
  billingEmail?: string;
  clinicSize?: number;
  needsMigrationSupport?: boolean;
  requestedGoLiveDate?: string;
  notes?: string;
};

type QuestionnaireRow = {
  id: string;
  prospectId: string;
  prospect: {
    id: string;
    clinicName: string;
    contactName: string;
    contactEmail: string;
    status: string;
    convertedOrganizationId: string | null;
  };
  status: string;
  invitedEmail: string;
  sentAt: string | null;
  openedAt: string | null;
  submittedAt: string | null;
  expiresAt: string | null;
  templateName?: string | null;
  templateId?: string | null;
  templateVersion?: number | null;
  templateDefinition?: QuestionnaireTemplateDefinition | null;
  createdAt: string;
  updatedAt: string;
  response: QuestionnaireSummary | Record<string, any> | null;
  responseSummary: QuestionnaireSummary | null;
  canConvert: boolean;
};

type QuestionnaireLeadOption = {
  id: string;
  clinicName: string;
  contactName: string;
  contactEmail: string;
  preferredLanguage: 'en' | 'es' | 'it';
  latestQuestionnaireTemplateId: string | null;
};

type QuestionnaireLeadPayload = {
  data: Array<{
    id: string;
    clinicName: string;
    contactName: string;
    contactEmail: string;
    preferredLanguage: 'en' | 'es' | 'it';
    latestQuestionnaire: { templateId: string | null } | null;
  }>;
};

type QuestionnaireTemplateRow = {
  id: string;
  name: string;
  description: string | null;
  status: string;
  isDefault: boolean;
  definition: QuestionnaireTemplateDefinition;
  pageCount?: number;
  questionCount?: number;
  questionnaireCount: number;
  createdAt: string;
  updatedAt: string;
};

type TemplateEditorState = {
  name: string;
  description: string;
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
  isDefault: boolean;
  definition: QuestionnaireTemplateDefinition;
};

type SendQuestionnaireState = {
  prospectId: string;
  invitedEmail: string;
  expiresInDays: string;
  templateId: string;
};

function formatDate(value?: string | null) {
  if (!value) return 'n/a';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'n/a';
  return date.toLocaleDateString();
}

function pillClass(value: string) {
  const normalized = value.toUpperCase();
  if (['ACTIVE', 'WON', 'SUBMITTED', 'TRIALING'].includes(normalized)) return 'pill pill-success';
  if (['PAST_DUE', 'QUESTIONNAIRE_SENT', 'QUESTIONNAIRE_SUBMITTED', 'QUALIFIED', 'OPENED', 'SENT'].includes(normalized)) return 'pill pill-warn';
  if (['SUSPENDED', 'LOST', 'CHURNED', 'CANCELLED', 'EXPIRED'].includes(normalized)) return 'pill pill-danger';
  return 'pill pill-info';
}

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="card p-5 lg:p-6">
      <div className="mb-5 flex items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-2 block font-medium text-slate-600">{label}</span>
      {children}
    </label>
  );
}

function EmptyState({ icon: Icon, title, text }: { icon: any; title: string; text: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 p-6 text-center">
      <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white">
        <Icon size={18} />
      </div>
      <div className="text-base font-semibold text-slate-900">{title}</div>
      <div className="mt-1 text-sm text-slate-500">{text}</div>
    </div>
  );
}

function renderResponseValue(value: unknown) {
  if (value === null || value === undefined || value === '') return 'n/a';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

function escapeHtml(value: unknown) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function defaultTemplateEditorState(): TemplateEditorState {
  return {
    name: DEFAULT_QUESTIONNAIRE_TEMPLATE.title,
    description: DEFAULT_QUESTIONNAIRE_TEMPLATE.description || '',
    status: 'ACTIVE',
    isDefault: true,
    definition: cloneQuestionnaireTemplateDefinition(DEFAULT_QUESTIONNAIRE_TEMPLATE),
  };
}

function templateStateFromRow(row: QuestionnaireTemplateRow): TemplateEditorState {
  return {
    name: row.name,
    description: row.description || '',
    status: row.status as TemplateEditorState['status'],
    isDefault: row.isDefault,
    definition: cloneQuestionnaireTemplateDefinition(row.definition),
  };
}

function normalizeOptions(value: string) {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function clonePresetTemplate(presetId: string) {
  const preset = QUESTIONNAIRE_TEMPLATE_PRESETS.find((item) => item.id === presetId);
  return preset ? cloneQuestionnaireTemplateDefinition(preset.definition) : cloneQuestionnaireTemplateDefinition(DEFAULT_QUESTIONNAIRE_TEMPLATE);
}

function getEditablePages(definition: QuestionnaireTemplateDefinition): QuestionnairePage[] {
  return getQuestionnairePages(definition);
}

function buildDefinitionFromPages(
  base: QuestionnaireTemplateDefinition,
  pages: QuestionnairePage[],
): QuestionnaireTemplateDefinition {
  return {
    title: base.title,
    description: base.description || '',
    submitLabel: base.submitLabel || '',
    pages: pages.map((page) => ({
      ...page,
      description: page.description || '',
      showWhen: page.showWhen ? { ...page.showWhen } : undefined,
      questions: page.questions.map((question) => ({
        ...question,
        options: question.options ? [...question.options] : [],
        validation: question.validation ? { ...question.validation } : {},
        showWhen: question.showWhen ? { ...question.showWhen } : undefined,
      })),
    })),
    questions: pages.flatMap((page) =>
      page.questions.map((question) => ({
        ...question,
        options: question.options ? [...question.options] : [],
        validation: question.validation ? { ...question.validation } : {},
        showWhen: question.showWhen ? { ...question.showWhen } : undefined,
      })),
    ),
  };
}

function collectPriorQuestions(pages: QuestionnairePage[], pageIndex: number, questionIndex?: number) {
  return pages.flatMap((page, currentPageIndex) => {
    if (currentPageIndex > pageIndex) return [];
    if (currentPageIndex < pageIndex) return page.questions;
    if (questionIndex === undefined) return [];
    return page.questions.slice(0, questionIndex);
  });
}

function updatePageList(
  pages: QuestionnairePage[],
  pageIndex: number,
  updater: (page: QuestionnairePage) => QuestionnairePage,
) {
  return pages.map((page, index) => (index === pageIndex ? updater(page) : page));
}

function DialogShell({
  title,
  description,
  children,
  onClose,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-3xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            {description ? <p className="mt-1 text-sm text-slate-500">{description}</p> : null}
          </div>
          <button type="button" className="rounded-lg border border-slate-200 px-2 py-1 text-sm text-slate-600 hover:bg-slate-50" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="max-h-[80vh] overflow-y-auto px-6 py-5">
          {children}
        </div>
      </div>
    </div>
  );
}

export function QuestionnaireTemplatesPageV2() {
  const { fetchJson, fetchApi } = useApiClient();
  const [templates, setTemplates] = useState<QuestionnaireTemplateRow[]>([]);
  const [selected, setSelected] = useState<QuestionnaireTemplateRow | null>(null);
  const [editor, setEditor] = useState<TemplateEditorState>(defaultTemplateEditorState());
  const [selectedPresetId, setSelectedPresetId] = useState(QUESTIONNAIRE_TEMPLATE_PRESETS[0]?.id || 'default');
  const [editorOpen, setEditorOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    fetchJson<{ data: QuestionnaireTemplateRow[] }>('/platform-admin/questionnaire-templates')
      .then((payload) => {
        setTemplates(payload.data);
        setMessage(null);
      })
      .catch((err) => setMessage(String(err?.message || 'Unable to load templates.')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [fetchJson]);

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((item) => [
      item.name,
      item.description,
      item.status,
      item.definition.title,
    ].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [search, templates]);

  const editablePages = useMemo(() => getEditablePages(editor.definition), [editor.definition]);

  const beginCreate = () => {
    setSelected(null);
    setEditor({
      name: QUESTIONNAIRE_TEMPLATE_PRESETS[0]?.name || DEFAULT_QUESTIONNAIRE_TEMPLATE.title,
      description: QUESTIONNAIRE_TEMPLATE_PRESETS[0]?.description || '',
      status: 'ACTIVE',
      isDefault: true,
      definition: clonePresetTemplate(selectedPresetId),
    });
    setEditorOpen(true);
    setMessage(null);
  };

  const beginEdit = (row: QuestionnaireTemplateRow) => {
    setSelected(row);
    setEditor(templateStateFromRow(row));
    setEditorOpen(true);
    setMessage(null);
  };

  const resetEditor = () => {
    setEditor(selected ? templateStateFromRow(selected) : defaultTemplateEditorState());
    setMessage(null);
  };

  const applyPreset = () => {
    const preset = QUESTIONNAIRE_TEMPLATE_PRESETS.find((item) => item.id === selectedPresetId);
    if (!preset) return;
    setSelected(null);
    setEditor({
      name: preset.name,
      description: preset.description,
      status: 'ACTIVE',
      isDefault: true,
      definition: clonePresetTemplate(preset.id),
    });
    setEditorOpen(true);
  };

  const updatePage = (pageIndex: number, patch: Partial<QuestionnairePage>) => {
    setEditor((current) => ({
      ...current,
      definition: buildDefinitionFromPages(current.definition, updatePageList(getEditablePages(current.definition), pageIndex, (page) => ({
        ...page,
        ...patch,
        description: patch.description !== undefined ? patch.description : page.description,
        showWhen:
          patch.showWhen === undefined
            ? page.showWhen
            : patch.showWhen
              ? {
                  questionKey: patch.showWhen.questionKey ?? page.showWhen?.questionKey ?? '',
                  operator: patch.showWhen.operator ?? page.showWhen?.operator ?? 'equals',
                  value: patch.showWhen.value ?? page.showWhen?.value ?? '',
                }
              : undefined,
      }))),
    }));
  };

  const updateQuestion = (pageIndex: number, questionIndex: number, patch: Partial<QuestionnaireField>) => {
    setEditor((current) => {
      const pages = updatePageList(getEditablePages(current.definition), pageIndex, (page) => ({
        ...page,
        questions: page.questions.map((question, index) => {
          if (index !== questionIndex) return question;
          const next = { ...question, ...patch };
          if (patch.type && patch.type !== 'select') {
            next.options = [];
          }
          return next;
        }),
      }));
      return { ...current, definition: buildDefinitionFromPages(current.definition, pages) };
    });
  };

  const updateQuestionValidation = (pageIndex: number, questionIndex: number, patch: Partial<QuestionnaireFieldValidation>) => {
    setEditor((current) => {
      const pages = updatePageList(getEditablePages(current.definition), pageIndex, (page) => ({
        ...page,
        questions: page.questions.map((question, index) => {
          if (index !== questionIndex) return question;
          return {
            ...question,
            validation: {
              ...(question.validation || {}),
              ...patch,
            },
          };
        }),
      }));
      return { ...current, definition: buildDefinitionFromPages(current.definition, pages) };
    });
  };

  const updateQuestionVisibility = (pageIndex: number, questionIndex: number, patch: Partial<QuestionnaireShowWhen>) => {
    setEditor((current) => {
      const pages = updatePageList(getEditablePages(current.definition), pageIndex, (page) => ({
        ...page,
        questions: page.questions.map((question, index) => {
          if (index !== questionIndex) return question;
          return {
            ...question,
            showWhen:
              patch.questionKey === undefined && patch.operator === undefined && patch.value === undefined
                ? undefined
                : {
                    questionKey: patch.questionKey ?? question.showWhen?.questionKey ?? '',
                    operator: patch.operator ?? question.showWhen?.operator ?? 'equals',
                    value: patch.value ?? question.showWhen?.value ?? '',
                  },
          };
        }),
      }));
      return { ...current, definition: buildDefinitionFromPages(current.definition, pages) };
    });
  };

  const addPage = () => {
    setEditor((current) => {
      const pages = [...getEditablePages(current.definition), createQuestionnairePage(getEditablePages(current.definition).length)];
      return { ...current, definition: buildDefinitionFromPages(current.definition, pages) };
    });
  };

  const removePage = (pageIndex: number) => {
    setEditor((current) => {
      const pages = getEditablePages(current.definition).filter((_, index) => index !== pageIndex);
      if (!pages.length) {
        pages.push(createQuestionnairePage(0));
      }
      return { ...current, definition: buildDefinitionFromPages(current.definition, pages) };
    });
  };

  const addQuestion = (pageIndex: number) => {
    setEditor((current) => {
      const pages = updatePageList(getEditablePages(current.definition), pageIndex, (page) => ({
        ...page,
        questions: [...page.questions, createQuestionnaireField(page.questions.length)],
      }));
      return { ...current, definition: buildDefinitionFromPages(current.definition, pages) };
    });
  };

  const removeQuestion = (pageIndex: number, questionIndex: number) => {
    setEditor((current) => {
      const pages = updatePageList(getEditablePages(current.definition), pageIndex, (page) => ({
        ...page,
        questions: page.questions.filter((_, index) => index !== questionIndex),
      }));
      return { ...current, definition: buildDefinitionFromPages(current.definition, pages) };
    });
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    const pages = editablePages.length ? editablePages : [createQuestionnairePage(0)];
    const definition = buildDefinitionFromPages(editor.definition, pages);
    const payload = {
      name: editor.name,
      description: editor.description || undefined,
      status: editor.status,
      isDefault: editor.isDefault,
      definition,
    };

    const res = await fetchApi(
      selected ? `/platform-admin/questionnaire-templates/${selected.id}` : '/platform-admin/questionnaire-templates',
      {
        method: selected ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );
    const response = await res.json().catch(() => ({} as any));
    setSaving(false);
    if (!res.ok) {
      setMessage(response?.message || 'Unable to save template.');
      return;
    }
    setMessage(selected ? 'Template updated.' : 'Template created.');
    setEditorOpen(false);
    load();
  };

  return (
    <div className="space-y-6">
      <SectionCard title="Questionnaire templates">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="min-w-[220px] flex-1">
            <input
              className={inputClass}
              placeholder="Search templates"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            onClick={load}
          >
            <RefreshCw size={15} />
            Refresh
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm text-white"
            onClick={beginCreate}
          >
            <Plus size={15} />
            Add new template
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500">Loading templates...</div>
        ) : filteredTemplates.length === 0 ? (
          <EmptyState
            icon={Layers3}
            title="No templates yet"
            text="Create the first questionnaire template to make the public form configurable."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-3 font-semibold">Template</th>
                  <th className="px-3 py-3 font-semibold">Status</th>
                  <th className="px-3 py-3 font-semibold">Usage</th>
                  <th className="px-3 py-3 font-semibold">Updated</th>
                  <th className="px-3 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-3">
                      <div className="font-semibold text-slate-900">{item.name}</div>
                      <div className="text-xs text-slate-500">{item.definition.title}</div>
                      {item.description ? <div className="mt-1 text-xs text-slate-400">{item.description}</div> : null}
                    </td>
                    <td className="px-3 py-3">
                      <span className={pillClass(item.status)}>{item.status}</span>
                      {item.isDefault ? <div className="mt-2 text-xs font-medium text-emerald-700">Default template</div> : null}
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      <div>{item.questionnaireCount} questionnaires</div>
                      <div className="text-xs text-slate-500">
                        {item.pageCount ?? getEditablePages(item.definition).length} pages • {item.questionCount ?? getQuestionnaireQuestions(item.definition).length} questions
                      </div>
                    </td>
                    <td className="px-3 py-3 text-slate-700">{formatDate(item.updatedAt)}</td>
                    <td className="px-3 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                          onClick={() => beginEdit(item)}
                        >
                          <Pencil size={14} />
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {editorOpen ? (
        <DialogShell
          title={selected ? 'Edit template' : 'Create template'}
          description="Create reusable questionnaire templates and control every question, page, and branch."
          onClose={() => setEditorOpen(false)}
        >
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[220px] flex-1">
                  <Field label="Template preset">
                    <select
                      className={inputClass}
                      value={selectedPresetId}
                      onChange={(event) => setSelectedPresetId(event.target.value)}
                    >
                      {QUESTIONNAIRE_TEMPLATE_PRESETS.map((preset) => (
                        <option key={preset.id} value={preset.id}>
                          {preset.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700"
                  onClick={applyPreset}
                >
                  <Layers3 size={15} />
                  Load preset
                </button>
              </div>
              <p className="mt-3 text-sm text-slate-500">
                Presets give you a fast starting point. You can still edit every page, question, validation rule, and branch condition before saving.
              </p>
            </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Template name">
              <input className={inputClass} value={editor.name} onChange={(event) => setEditor({ ...editor, name: event.target.value })} />
            </Field>
            <Field label="Status">
              <select className={inputClass} value={editor.status} onChange={(event) => setEditor({ ...editor, status: event.target.value as TemplateEditorState['status'] })}>
                <option value="DRAFT">DRAFT</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="ARCHIVED">ARCHIVED</option>
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label="Description">
                <textarea className={`${inputClass} min-h-[84px]`} value={editor.description} onChange={(event) => setEditor({ ...editor, description: event.target.value })} />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 md:col-span-2">
              <input type="checkbox" checked={editor.isDefault} onChange={(event) => setEditor({ ...editor, isDefault: event.target.checked })} />
              Make this the default template for outgoing questionnaires
            </label>
          </div>

          <div className="rounded-2xl border border-slate-100 p-4 space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Questionnaire title">
                <input className={inputClass} value={editor.definition.title} onChange={(event) => setEditor({
                  ...editor,
                  definition: { ...editor.definition, title: event.target.value },
                })} />
              </Field>
              <Field label="Submit label">
                <input className={inputClass} value={editor.definition.submitLabel || ''} onChange={(event) => setEditor({
                  ...editor,
                  definition: { ...editor.definition, submitLabel: event.target.value },
                })} />
              </Field>
              <div className="md:col-span-2">
                <Field label="Intro description">
                  <textarea className={`${inputClass} min-h-[84px]`} value={editor.definition.description || ''} onChange={(event) => setEditor({
                    ...editor,
                    definition: { ...editor.definition, description: event.target.value },
                  })} />
                </Field>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Pages</h3>
              <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700" onClick={addPage}>
                <Plus size={15} />
                Add page
              </button>
            </div>

            <div className="space-y-4">
              {editablePages.map((page, pageIndex) => {
                const pageCandidates = collectPriorQuestions(editablePages, pageIndex);
                return (
                  <div key={`${page.key}-${pageIndex}`} className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4 space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-semibold text-slate-900">Page {pageIndex + 1}</div>
                        <div className="text-xs text-slate-500">{page.questions.length} questions</div>
                      </div>
                      <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-600" onClick={() => removePage(pageIndex)}>
                        <Trash2 size={14} />
                        Remove page
                      </button>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                      <Field label="Page key">
                        <input className={inputClass} value={page.key} onChange={(event) => updatePage(pageIndex, { key: event.target.value })} />
                      </Field>
                      <Field label="Page title">
                        <input className={inputClass} value={page.title} onChange={(event) => updatePage(pageIndex, { title: event.target.value })} />
                      </Field>
                      <div className="md:col-span-2">
                        <Field label="Page description">
                          <textarea className={`${inputClass} min-h-[72px]`} value={page.description || ''} onChange={(event) => updatePage(pageIndex, { description: event.target.value })} />
                        </Field>
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-3">
                      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Conditional display</div>
                      <div className="grid gap-3 md:grid-cols-3">
                        <Field label="Show when question">
                          <select
                            className={inputClass}
                            value={page.showWhen?.questionKey || ''}
                            onChange={(event) => updatePage(pageIndex, { showWhen: event.target.value ? { questionKey: event.target.value, operator: page.showWhen?.operator || 'equals', value: page.showWhen?.value || '' } : undefined })}
                          >
                            <option value="">Always visible</option>
                            {pageCandidates.map((candidate) => (
                              <option key={candidate.key} value={candidate.key}>{candidate.label || candidate.key}</option>
                            ))}
                          </select>
                        </Field>
                        <Field label="Operator">
                          <select
                            className={inputClass}
                            value={page.showWhen?.operator || 'equals'}
                            onChange={(event) => updatePage(pageIndex, { showWhen: { questionKey: page.showWhen?.questionKey || '', operator: event.target.value as QuestionnaireShowWhen['operator'], value: page.showWhen?.value || '' } })}
                            disabled={!page.showWhen?.questionKey}
                          >
                            {QUESTIONNAIRE_CONDITION_OPERATORS.map((operator) => (
                              <option key={operator} value={operator}>{operator}</option>
                            ))}
                          </select>
                        </Field>
                        <Field label="Condition value">
                          <input
                            className={inputClass}
                            value={page.showWhen?.value || ''}
                            onChange={(event) => updatePage(pageIndex, { showWhen: { questionKey: page.showWhen?.questionKey || '', operator: page.showWhen?.operator || 'equals', value: event.target.value } })}
                            placeholder="true, yes, or a specific value"
                            disabled={!page.showWhen?.questionKey || ['is_truthy', 'is_falsy'].includes(page.showWhen?.operator || '')}
                          />
                        </Field>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Questions</h4>
                      <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700" onClick={() => addQuestion(pageIndex)}>
                        <Plus size={15} />
                        Add question
                      </button>
                    </div>

                    <div className="space-y-4">
                      {page.questions.map((question, questionIndex) => {
                        const candidateQuestions = collectPriorQuestions(editablePages, pageIndex, questionIndex);
                        return (
                          <div key={`${question.key}-${pageIndex}-${questionIndex}`} className="rounded-2xl border border-slate-100 bg-white p-4 space-y-3">
                            <div className="flex items-center justify-between gap-3">
                              <div className="font-semibold text-slate-900">Question {questionIndex + 1}</div>
                              <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-600" onClick={() => removeQuestion(pageIndex, questionIndex)}>
                                <Trash2 size={14} />
                                Remove
                              </button>
                            </div>
                            <div className="grid gap-3 md:grid-cols-2">
                              <Field label="Key">
                                <input className={inputClass} value={question.key} onChange={(event) => updateQuestion(pageIndex, questionIndex, { key: event.target.value })} />
                              </Field>
                              <Field label="Type">
                                <select className={inputClass} value={question.type} onChange={(event) => updateQuestion(pageIndex, questionIndex, { type: event.target.value as QuestionnaireField['type'] })}>
                                  {QUESTIONNAIRE_FIELD_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                                </select>
                              </Field>
                              <Field label="Label">
                                <input className={inputClass} value={question.label} onChange={(event) => updateQuestion(pageIndex, questionIndex, { label: event.target.value })} />
                              </Field>
                              <Field label="Placeholder">
                                <input className={inputClass} value={question.placeholder || ''} onChange={(event) => updateQuestion(pageIndex, questionIndex, { placeholder: event.target.value })} />
                              </Field>
                              <div className="md:col-span-2">
                                <Field label="Help text">
                                  <input className={inputClass} value={question.helpText || ''} onChange={(event) => updateQuestion(pageIndex, questionIndex, { helpText: event.target.value })} />
                                </Field>
                              </div>
                              <div className="md:col-span-2 rounded-xl border border-slate-200 bg-white p-3 space-y-3">
                                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Validation</div>
                                {(question.type === 'short_text' || question.type === 'long_text' || question.type === 'email' || question.type === 'phone') ? (
                                  <div className="grid gap-3 md:grid-cols-2">
                                    <Field label="Min length">
                                      <input
                                        className={inputClass}
                                        type="number"
                                        min="0"
                                        value={question.validation?.minLength ?? ''}
                                        onChange={(event) => updateQuestionValidation(pageIndex, questionIndex, { minLength: event.target.value ? Number(event.target.value) : undefined })}
                                      />
                                    </Field>
                                    <Field label="Max length">
                                      <input
                                        className={inputClass}
                                        type="number"
                                        min="0"
                                        value={question.validation?.maxLength ?? ''}
                                        onChange={(event) => updateQuestionValidation(pageIndex, questionIndex, { maxLength: event.target.value ? Number(event.target.value) : undefined })}
                                      />
                                    </Field>
                                    {(question.type === 'short_text' || question.type === 'long_text' || question.type === 'phone') ? (
                                      <>
                                        <Field label="Pattern">
                                          <input
                                            className={inputClass}
                                            value={question.validation?.pattern || ''}
                                            onChange={(event) => updateQuestionValidation(pageIndex, questionIndex, { pattern: event.target.value || undefined })}
                                            placeholder="^[A-Z]{2}[0-9]+$"
                                          />
                                        </Field>
                                        <Field label="Pattern message">
                                          <input
                                            className={inputClass}
                                            value={question.validation?.patternMessage || ''}
                                            onChange={(event) => updateQuestionValidation(pageIndex, questionIndex, { patternMessage: event.target.value || undefined })}
                                          />
                                        </Field>
                                      </>
                                    ) : null}
                                  </div>
                                ) : null}
                                {question.type === 'number' ? (
                                  <div className="grid gap-3 md:grid-cols-2">
                                    <Field label="Min value">
                                      <input
                                        className={inputClass}
                                        type="number"
                                        value={question.validation?.minValue ?? ''}
                                        onChange={(event) => updateQuestionValidation(pageIndex, questionIndex, { minValue: event.target.value ? Number(event.target.value) : undefined })}
                                      />
                                    </Field>
                                    <Field label="Max value">
                                      <input
                                        className={inputClass}
                                        type="number"
                                        value={question.validation?.maxValue ?? ''}
                                        onChange={(event) => updateQuestionValidation(pageIndex, questionIndex, { maxValue: event.target.value ? Number(event.target.value) : undefined })}
                                      />
                                    </Field>
                                  </div>
                                ) : null}
                              </div>
                              <div className="md:col-span-2 rounded-xl border border-slate-200 bg-white p-3 space-y-3">
                                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Conditional display</div>
                                <div className="grid gap-3 md:grid-cols-3">
                                  <Field label="Show when question">
                                    <select
                                      className={inputClass}
                                      value={question.showWhen?.questionKey || ''}
                                      onChange={(event) => updateQuestionVisibility(pageIndex, questionIndex, { questionKey: event.target.value || undefined })}
                                    >
                                      <option value="">Always visible</option>
                                      {candidateQuestions.map((candidate) => (
                                        <option key={candidate.key} value={candidate.key}>{candidate.label || candidate.key}</option>
                                      ))}
                                    </select>
                                  </Field>
                                  <Field label="Operator">
                                    <select
                                      className={inputClass}
                                      value={question.showWhen?.operator || 'equals'}
                                      onChange={(event) => updateQuestionVisibility(pageIndex, questionIndex, { operator: event.target.value as QuestionnaireShowWhen['operator'] })}
                                      disabled={!question.showWhen?.questionKey}
                                    >
                                      {QUESTIONNAIRE_CONDITION_OPERATORS.map((operator) => (
                                        <option key={operator} value={operator}>{operator}</option>
                                      ))}
                                    </select>
                                  </Field>
                                  <Field label="Condition value">
                                    <input
                                      className={inputClass}
                                      value={question.showWhen?.value || ''}
                                      onChange={(event) => updateQuestionVisibility(pageIndex, questionIndex, { value: event.target.value })}
                                      placeholder="true, yes, or a specific value"
                                      disabled={!question.showWhen?.questionKey || ['is_truthy', 'is_falsy'].includes(question.showWhen?.operator || '')}
                                    />
                                  </Field>
                                </div>
                              </div>
                              {question.type === 'select' ? (
                                <div className="md:col-span-2">
                                  <Field label="Options">
                                    <input
                                      className={inputClass}
                                      value={(question.options || []).join(', ')}
                                      onChange={(event) => updateQuestion(pageIndex, questionIndex, { options: normalizeOptions(event.target.value) })}
                                      placeholder="Option 1, Option 2, Option 3"
                                    />
                                  </Field>
                                </div>
                              ) : null}
                              <label className="flex items-center gap-2 text-sm text-slate-600">
                                <input type="checkbox" checked={Boolean(question.required)} onChange={(event) => updateQuestion(pageIndex, questionIndex, { required: event.target.checked })} />
                                Required
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Pencil size={15} />
              {selected ? `Editing ${selected.name}` : 'Creating a new template'}
            </div>
            <div className="flex items-center gap-3">
              <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700" onClick={resetEditor}>
                <Copy size={15} />
                Reset
              </button>
              <button type="button" className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm text-white disabled:opacity-60" onClick={save} disabled={saving || editablePages.length === 0}>
                <Save size={15} />
                {saving ? 'Saving...' : 'Save template'}
              </button>
            </div>
          </div>

          {message ? <div className="text-sm text-slate-600">{message}</div> : null}
          </div>
        </DialogShell>
      ) : null}
    </div>
  );
}

export function QuestionnairesPageV2() {
  const { fetchJson, fetchApi } = useApiClient();
  const navigate = useNavigate();
  const [rows, setRows] = useState<QuestionnaireRow[]>([]);
  const [prospects, setProspects] = useState<QuestionnaireLeadOption[]>([]);
  const [templates, setTemplates] = useState<QuestionnaireTemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<QuestionnaireRow | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendMessage, setSendMessage] = useState<string | null>(null);
  const [sendForm, setSendForm] = useState<SendQuestionnaireState>({
    prospectId: '',
    invitedEmail: '',
    expiresInDays: '14',
    templateId: '',
  });

  const load = () => {
    setLoading(true);
    Promise.all([
      fetchJson<{ data: QuestionnaireRow[] }>('/platform-admin/questionnaires'),
      fetchJson<{ data: QuestionnaireTemplateRow[] }>('/platform-admin/questionnaire-templates'),
      fetchJson<QuestionnaireLeadPayload>('/platform-admin/prospects'),
    ])
      .then(([questionnairesPayload, templatesPayload, prospectsPayload]) => {
        setRows(questionnairesPayload.data);
        setTemplates(templatesPayload.data);
        setProspects(
          prospectsPayload.data.map((item) => ({
            id: item.id,
            clinicName: item.clinicName,
            contactName: item.contactName,
            contactEmail: item.contactEmail,
            preferredLanguage: item.preferredLanguage,
            latestQuestionnaireTemplateId: item.latestQuestionnaire?.templateId ?? null,
          })),
        );
        setSelected((current) => questionnairesPayload.data.find((item) => item.id === current?.id) || questionnairesPayload.data[0] || null);
        const defaultTemplate = templatesPayload.data.find((item) => item.isDefault && item.status === 'ACTIVE')
          || templatesPayload.data.find((item) => item.status === 'ACTIVE')
          || templatesPayload.data[0]
          || null;
        const defaultLead = prospectsPayload.data[0] || null;
        setSendForm({
          prospectId: defaultLead?.id || '',
          invitedEmail: defaultLead?.contactEmail || '',
          expiresInDays: '14',
          templateId: defaultTemplate?.id || '',
        });
        setError(null);
      })
      .catch((err) => setError(String(err?.message || 'Unable to load questionnaires.')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [fetchJson]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((item) => {
      const haystack = [
        item.prospect.clinicName,
        item.prospect.contactName,
        item.prospect.contactEmail,
        item.prospect.status,
        item.status,
        item.invitedEmail,
        item.templateName,
        item.responseSummary?.clinicLegalName,
        item.responseSummary?.preferredSubdomain,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, search]);

  const selectedLead = useMemo(
    () => prospects.find((item) => item.id === sendForm.prospectId) || null,
    [prospects, sendForm.prospectId],
  );

  useEffect(() => {
    if (!sendOpen) return;
    if (!selectedLead && prospects.length) {
      setSendForm((current) => ({
        ...current,
        prospectId: prospects[0].id,
        invitedEmail: prospects[0].contactEmail,
        templateId: current.templateId || templates.find((item) => item.isDefault && item.status === 'ACTIVE')?.id || templates.find((item) => item.status === 'ACTIVE')?.id || templates[0]?.id || '',
      }));
    }
  }, [prospects, selectedLead, sendOpen, templates]);

  const metrics = useMemo(() => {
    const submitted = rows.filter((item) => item.status === 'SUBMITTED').length;
    const opened = rows.filter((item) => item.status === 'OPENED' || item.status === 'SUBMITTED').length;
    const expired = rows.filter((item) => item.status === 'EXPIRED').length;
    return [
      { label: 'Questionnaires', value: rows.length, icon: Inbox },
      { label: 'Submitted', value: submitted, icon: ClipboardCheck },
      { label: 'Opened', value: opened, icon: Eye },
      { label: 'Expired', value: expired, icon: Search },
    ];
  }, [rows]);

  const exportCsv = async () => {
    const res = await fetchApi('/platform-admin/questionnaires/export');
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      setError(text || 'Unable to export questionnaires.');
      return;
    }

    const csv = await res.text();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `questionnaires-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const openSendDialog = () => {
    const defaultTemplate = templates.find((item) => item.isDefault && item.status === 'ACTIVE')
      || templates.find((item) => item.status === 'ACTIVE')
      || templates[0]
      || null;
    const defaultLead = prospects[0] || null;
    setSendForm({
      prospectId: defaultLead?.id || '',
      invitedEmail: defaultLead?.contactEmail || '',
      expiresInDays: '14',
      templateId: defaultTemplate?.id || '',
    });
    setSendMessage(null);
    setSendOpen(true);
  };

  const submitQuestionnaire = async (event: FormEvent) => {
    event.preventDefault();
    if (!sendForm.prospectId) return;
    setSendMessage(null);
    const res = await fetchApi(`/platform-admin/prospects/${sendForm.prospectId}/questionnaires`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        invitedEmail: sendForm.invitedEmail || undefined,
        expiresInDays: Number(sendForm.expiresInDays || 14),
        templateId: sendForm.templateId || undefined,
      }),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      setSendMessage(payload?.message || 'Unable to send questionnaire.');
      return;
    }
    setSendMessage(`Questionnaire sent. Link: ${payload?.data?.formUrl}`);
    setSendOpen(false);
    load();
  };

  const printSelected = () => {
    if (!selected) return;
    const rawResponse = renderResponseValue(selected.response);
    const summary = selected.responseSummary;
    const html = `
      <html>
        <head>
          <title>${escapeHtml(selected.prospect.clinicName)} questionnaire</title>
          <style>
            body { font-family: Inter, ui-sans-serif, system-ui, sans-serif; color: #0f172a; margin: 32px; }
            h1 { margin: 0 0 8px 0; font-size: 24px; }
            .muted { color: #64748b; }
            .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 18px; margin-top: 20px; }
            .item { padding: 12px; border: 1px solid #e2e8f0; border-radius: 12px; }
            .label { font-size: 12px; color: #64748b; text-transform: uppercase; letter-spacing: .08em; margin-bottom: 4px; }
            pre { white-space: pre-wrap; word-break: break-word; padding: 16px; border: 1px solid #e2e8f0; border-radius: 12px; background: #f8fafc; }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(selected.prospect.clinicName)}</h1>
          <div class="muted">${escapeHtml(selected.prospect.contactName)} • ${escapeHtml(selected.prospect.contactEmail)}</div>
          <div class="grid">
            <div class="item"><div class="label">Status</div><div>${escapeHtml(selected.status)}</div></div>
            <div class="item"><div class="label">Template</div><div>${escapeHtml(selected.templateName || 'Default template')}</div></div>
            <div class="item"><div class="label">Invited</div><div>${escapeHtml(selected.invitedEmail)}</div></div>
            <div class="item"><div class="label">Submitted</div><div>${escapeHtml(formatDate(selected.submittedAt))}</div></div>
            <div class="item"><div class="label">Opened</div><div>${escapeHtml(formatDate(selected.openedAt))}</div></div>
            <div class="item"><div class="label">Prospect status</div><div>${escapeHtml(selected.prospect.status)}</div></div>
          </div>
          <h2 style="margin: 24px 0 12px 0; font-size: 18px;">Summary</h2>
          <div class="grid">
            <div class="item"><div class="label">Legal clinic name</div><div>${escapeHtml(summary?.clinicLegalName || 'n/a')}</div></div>
            <div class="item"><div class="label">Preferred subdomain</div><div>${escapeHtml(summary?.preferredSubdomain || 'n/a')}</div></div>
            <div class="item"><div class="label">Timezone</div><div>${escapeHtml(summary?.timezone || 'n/a')}</div></div>
            <div class="item"><div class="label">Country</div><div>${escapeHtml(summary?.country || 'n/a')}</div></div>
            <div class="item"><div class="label">Billing contact</div><div>${escapeHtml(summary?.billingContactName || 'n/a')}</div></div>
            <div class="item"><div class="label">Billing email</div><div>${escapeHtml(summary?.billingEmail || 'n/a')}</div></div>
            <div class="item"><div class="label">Clinic size</div><div>${escapeHtml(summary?.clinicSize ?? 'n/a')}</div></div>
            <div class="item"><div class="label">Migration support</div><div>${escapeHtml(summary?.needsMigrationSupport === undefined ? 'n/a' : summary?.needsMigrationSupport ? 'Yes' : 'No')}</div></div>
            <div class="item" style="grid-column: 1 / -1;"><div class="label">Notes</div><div>${escapeHtml(summary?.notes || 'n/a')}</div></div>
          </div>
          <h2 style="margin: 24px 0 12px 0; font-size: 18px;">Raw payload</h2>
          <pre>${escapeHtml(rawResponse)}</pre>
          <script>window.print();</script>
        </body>
      </html>
    `;
    const win = window.open('', '_blank', 'noopener,noreferrer,width=1200,height=900');
    if (!win) return;
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  return (
    <div className="space-y-6">
      <SectionCard title="Questionnaire inbox">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <input
              className={inputClass}
              placeholder="Search by clinic, contact, status, or subdomain"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            onClick={load}
          >
            <RefreshCw size={15} />
            Refresh
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            onClick={exportCsv}
          >
            <Download size={15} />
            Export CSV
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm text-white"
            onClick={openSendDialog}
          >
            <Plus size={15} />
            Add new questionnaire
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            onClick={() => navigate('/questionnaire-templates')}
          >
            <Pencil size={15} />
            Manage templates
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            onClick={() => navigate('/leads')}
          >
            <UserRound size={15} />
            Open leads
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500">Loading questionnaires...</div>
        ) : error ? (
          <div className="text-sm text-rose-600">{error}</div>
        ) : filteredRows.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="No questionnaires yet"
            text="Send a questionnaire from a lead and the responses will appear here."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-3 font-semibold">Clinic</th>
                  <th className="px-3 py-3 font-semibold">Lead</th>
                  <th className="px-3 py-3 font-semibold">Questionnaire</th>
                  <th className="px-3 py-3 font-semibold">Response</th>
                  <th className="px-3 py-3 font-semibold">Updated</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((item) => (
                  <tr
                    key={item.id}
                    className={`cursor-pointer border-b border-slate-100 hover:bg-slate-50 ${selected?.id === item.id ? 'bg-slate-50' : ''}`}
                    onClick={() => setSelected(item)}
                  >
                    <td className="px-3 py-3">
                      <div className="font-semibold text-slate-900">{item.prospect.clinicName}</div>
                      <div className="text-xs text-slate-500">Prospect ID {item.prospectId}</div>
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      <div>{item.prospect.contactName}</div>
                      <div className="text-xs text-slate-500">{item.prospect.contactEmail}</div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-slate-700">{item.status}</div>
                      <div className="mt-1">
                        <span className={pillClass(item.status)}>{item.status}</span>
                      </div>
                      <div className="mt-2 text-xs text-slate-500">{item.templateName || 'Default template'}</div>
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      <div>{item.responseSummary?.clinicLegalName || item.response?.['clinicLegalName'] || 'Awaiting response'}</div>
                      <div className="text-xs text-slate-500">
                        {item.submittedAt ? `Submitted ${formatDate(item.submittedAt)}` : `Opened ${formatDate(item.openedAt)}`}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-slate-700">{formatDate(item.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {sendOpen ? (
        <DialogShell
          title="Add new questionnaire"
          description="Send an onboarding questionnaire directly from the questionnaire inbox."
          onClose={() => setSendOpen(false)}
        >
          <form className="grid gap-4 md:grid-cols-2" onSubmit={submitQuestionnaire}>
            <Field label="Lead">
              <select
                className={inputClass}
                value={sendForm.prospectId}
                onChange={(event) => {
                  const prospect = prospects.find((item) => item.id === event.target.value) || null;
                  const defaultTemplate = templates.find((item) => item.isDefault && item.status === 'ACTIVE')
                    || templates.find((item) => item.status === 'ACTIVE')
                    || templates[0]
                    || null;
                  setSendForm({
                    prospectId: prospect?.id || '',
                    invitedEmail: prospect?.contactEmail || '',
                    expiresInDays: sendForm.expiresInDays,
                    templateId: prospect?.latestQuestionnaireTemplateId || defaultTemplate?.id || '',
                  });
                }}
              >
                <option value="">Select a lead</option>
                {prospects.map((prospect) => (
                  <option key={prospect.id} value={prospect.id}>
                    {prospect.clinicName} • {prospect.contactName}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Recipient email"><input className={inputClass} value={sendForm.invitedEmail} onChange={(e) => setSendForm({ ...sendForm, invitedEmail: e.target.value })} /></Field>
            <Field label="Expires in days"><input className={inputClass} type="number" min="1" max="60" value={sendForm.expiresInDays} onChange={(e) => setSendForm({ ...sendForm, expiresInDays: e.target.value })} /></Field>
            <div className="md:col-span-2">
              <Field label="Template">
                <select className={inputClass} value={sendForm.templateId} onChange={(e) => setSendForm({ ...sendForm, templateId: e.target.value })}>
                  <option value="">Use active default</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}{template.isDefault ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="md:col-span-2 flex justify-end gap-3">
              <button type="button" className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-700" onClick={() => setSendOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
                Send questionnaire
              </button>
            </div>
          </form>
          {sendMessage ? <div className="mt-4 text-sm text-slate-600">{sendMessage}</div> : null}
        </DialogShell>
      ) : null}
    </div>
  );
}
